import { consume, ContextType } from '@lit/context';
import { SlDropdown, SlInput } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { api } from '../api.js';
import { contexts, FKProjectChanged } from '../contexts.js';

type ZoneInfo = {
  timezone: string,
  offset: string,
  name: string,
  offsetNanoseconds: number,
  city: string,
  searchTokens: string[],
}

const allZones: ZoneInfo[] = Intl.supportedValuesOf('timeZone').map(timezone => {
  const city = (timezone.split('/').pop() ?? timezone).replace(/_/g, ' ');
  const zdt = Temporal.Now.zonedDateTimeISO(timezone);
  const name = new Intl.DateTimeFormat('en', { timeZone: timezone, timeZoneName: 'long' })
    .formatToParts(new Date())
    .find(p => p.type === 'timeZoneName')!.value;
  const offset = 'UTC' + zdt.offset;
  const searchTokens = [name.toLowerCase(), city.toLowerCase(), offset.toLowerCase()];
  return { name, city, offset, offsetNanoseconds: zdt.offsetNanoseconds, timezone, searchTokens };
});
allZones.push({
  city: 'UTC',
  timezone: 'UTC',
  offset: 'UTC+00:00',
  offsetNanoseconds: 0,
  searchTokens: ['utc'],
  name: 'Universal',
});

allZones.sort((z1, z2) => z1.offsetNanoseconds - z2.offsetNanoseconds);

/** How a timezone reads in the UI: its current UTC offset and its city, like "UTC+02:00 · Berlin". */
export function timezoneLabel(timezone: string): string {
  const offset = Temporal.Now.zonedDateTimeISO(timezone).offset;
  const city = (timezone.split('/').pop() ?? timezone).replace(/_/g, ' ');
  return `UTC${offset} · ${city}`;
}


@customElement('timezone-selector')
class TimezoneSelector extends LitElement {
  @property({ type: Boolean }) disabled?: boolean;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;

  @state() private _timezoneFilter?: string;

  @query('sl-dropdown') private _dropdown?: SlDropdown;
  @query('sl-input') private _input?: SlInput;

  render() {
    const project = this._project;
    if (!project)
      return nothing;
    const selected = allZones.find(zone => zone.timezone === project.timezone);
    if (!selected)
      return nothing;
    const needle = (this._timezoneFilter ?? '').toLowerCase();

    const filtered = allZones.filter(zone => zone.searchTokens.some(token => token.includes(needle)));

    if (this.disabled)
      return html`<div class=timezone-text>${timezoneLabel(project.timezone)}</div>`
    return html`
      <sl-dropdown ?disabled=${this.disabled}>
        <sl-button slot=trigger caret>${timezoneLabel(project.timezone)}</sl-button>
        <v-box class=timezone-picker>
          <sl-input
            value=${this._timezoneFilter}
            placeholder='Find a timezone...'
            @input=${(e: InputEvent) => {
              this._timezoneFilter = this._input?.value;
            }}>
          </sl-input>
          <sl-divider style="--spacing: var(--sl-spacing-3x-small)"></sl-divider>
          <section class=zones-list>
            ${filtered.length ? filtered.map(zone => this._renderZone(zone, needle)) : html`
              <div class=nothing-found>Nothing to show</div>
            `}
          </section>
        </v-box>
      </sl-dropdown>
    `;
  }

  private _renderZone({ timezone, offset, name, city }: ZoneInfo, needle: string) {
    return html`
      <button class=${classMap({
        zone: true,
      })} @click=${() => this._selectTimezone(timezone)}>
        <hl-span class=offset .text=${offset} .hl=${highlight(offset.toLowerCase(), needle)}></hl-span>
        <hl-span class=city .text=${city} .hl=${highlight(city.toLowerCase(), needle)}></hl-span>
      </button>
    `;
  }

  private async _selectTimezone(timezone: string) {
    if (!this._project)
      return;
    this._dropdown?.hide();
    if (this._input)
      this._input.value = '';
    this._timezoneFilter = '';
    await api.project.setProjectDefaultTimezone.POST({
      orgSlug: this._project.org.orgSlug,
      projectSlug: this._project.projectSlug,
      defaultTimezone: timezone,
    });
    this.dispatchEvent(new FKProjectChanged({
      orgSlug: this._project.org.orgSlug,
      projectSlug: this._project.projectSlug,
    }));
  }

  static styles = [css`

    :host([subtle]) {
      sl-button::part(base), .timezone-text {
        background: none;
        border: none;
        color: var(--sl-color-neutral-400);
      }
    }

    .timezone-text {
      font-size: var(--sl-font-size-small);
    }

    .header {
      margin: var(--sl-spacing-small);
    }

    .nothing-found {
      margin: var(--sl-spacing-large) 0;
      display: flex;
      align-items: center;
      justify-content: center;
      grid-column: 1/-1;
    }

    .timezone-picker {
      min-width: 300px;
      background-color: white;
      padding: var(--sl-spacing-small) 0;
      border: 1px solid var(--fk-color-border);
      box-shadow: var(--fk-shadow-floating-small);
      border-radius: var(--sl-border-radius-large);

      .zones-list {
        max-height: 400px;
        overflow: auto;
        display: grid;
        gap: 0 var(--sl-spacing-small);
        grid-template-columns: auto 1fr;
      }

      .zone {
        appearance: none;
        margin: none;
        padding: none;
        background: none;
        border: none;
        text-align: left;

        display: grid;
        grid-column: 1/-1;
        grid-template-columns: subgrid;
        margin: 0 var(--sl-spacing-x-small);
        padding: var(--sl-spacing-2x-small) var(--sl-spacing-small);
        border-radius: var(--sl-border-radius-medium);
        cursor: pointer;

        &:hover {
          background-color: var(--sl-color-neutral-100);
        }

        .offset {
          color: var(--sl-color-neutral-500);
          font-family: var(--sl-font-mono);
        }

        .city {
          font-weight: var(--sl-font-weight-semibold);
          font-size: var(--sl-font-size-medium);
        }

        .region {
          font-size: var(--sl-font-size-small);
          color: var(--sl-color-neutral-500);
        }
      }
    }

    sl-input {
      margin: 0 var(--sl-spacing-small);
    }

  `];
}


function highlight(text: string, needle: string|undefined) {
  if (!needle)
    return undefined;
  const i = text.toLowerCase().indexOf(needle);
  // Inclusive [from, to] per the Ranges convention.
  return (i !== -1) ? [i, i + needle.length - 1] : undefined;
}